import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, TextField, InputAdornment } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AppsIcon from "@mui/icons-material/Apps";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchDepartment, releaseApplicationFromDepartment } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import BuildDepartmentDrawer from "../../components/build/BuildDepartmentDrawer";

export default function BuildDepartmentApplicationListPage() {
  const { businessDomainId, businessUnitId, departmentId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [releasing, setReleasing] = useState(false);

  const basePath = `/build/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}`;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const dept = await fetchDepartment(departmentId);
      setRows(Array.isArray(dept.associatedApplicationCategories) ? dept.associatedApplicationCategories : []);
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRelease = async (appCat) => {
    setReleasing(true);
    try {
      await releaseApplicationFromDepartment(departmentId, appCat);
      notify.success(t("message.release.success"));
      loadData();
    } catch (e) { notify.apiError(e); }
    finally { setReleasing(false); }
  };

  const filteredRows = searchText
    ? rows.filter((r) => r.applicationCategoryName?.toLowerCase().includes(searchText.toLowerCase()))
    : rows;

  const columns = [
    { field: "applicationCategoryName", headerName: t("applicationCategory.name"), flex: 1, minWidth: 200 },
    {
      field: "_actions",
      headerName: "",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          color="error"
          disabled={releasing}
          onClick={(e) => { e.stopPropagation(); handleRelease(params.row); }}
        >
          {t("common.release")}
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <BuildDepartmentDrawer />
          <Typography variant="h6">{t("build.department.application.list.title")}</Typography>
        <TextField size="small" placeholder={t("common.search")} value={searchText} onChange={(e) => setSearchText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        </Box>
        <Button
          variant="contained"
          startIcon={<AppsIcon />}
          onClick={() => navigate(`${basePath}/applications/assign`)}
        >
          {t("common.assign")}
        </Button>
      </Box>
      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(r) => r.applicationCategoryId}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
      />
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(`/build/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}`)}>{t("common.back")}</Button>
      </Box>
    </Box>
  );
}
